import React from 'react';
import { FieldTemplateProps, UiSchema } from '@rjsf/core';
import { EIcMetadataXSource, JEDEAI_METADATA_X_SOURCE_LIST } from '@sgdocs/client';
import './JsonSchemaForm.css';
import { BooleanJsonSchemaWidget } from './widgets/BooleanJsonSchemaWidget';
import { DateJsonSchemaWidget } from './widgets/DateJsonSchemaWidget';
import { DateRangeJsonSchemaWidget } from './widgets/DateRangeJsonSchemaWidget';
import { IcAccountElementaryClientJsonSchemaWidget } from './widgets/ic-client/IcAccountElementaryClientJsonSchemaWidget';
import { IcAccountJsonSchemaWidget } from './widgets/ic-client/IcAccountJsonSchemaWidget';
import { IcAccountLegalEntityJsonSchemaWidget } from './widgets/ic-client/IcAccountLegalEntityJsonSchemaWidget';
import { IcAccountsElementaryClientJsonSchemaWidget } from './widgets/ic-client/IcAccountsElementaryClientJsonSchemaWidget';
import { IcAccountsJsonSchemaWidget } from './widgets/ic-client/IcAccountsJsonSchemaWidget';
import { IcAccountsLegalEntityJsonSchemaWidget } from './widgets/ic-client/IcAccountsLegalEntityJsonSchemaWidget';
import { flattenIcContacts, IcContactsJsonSchemaWidget } from './widgets/IcContactsJsonSchemaWidget';
import {
  EliotBasketJsonSchemaWidget,
  EliotBasketSearchJsonSchemaWidget,
  EliotFlowJsonSchemaWidget,
  EliotFlowSearchJsonSchemaWidget,
  GalaxyProductJsonSchemaWidget,
  GalaxyProductSearchJsonSchemaWidget,
  MaestroCareCounterpartyJsonSchemaWidget,
  MaestroCareCounterpartySearchJsonSchemaWidget,
  MaestroCounterpartyJsonSchemaWidget,
  MaestroCounterpartySearchJsonSchemaWidget,
  MaestroLegalEntityJsonSchemaWidget,
  MaestroLegalEntitySearchJsonSchemaWidget,
  SparkMontageJsonSchemaWidget,
  SparkMontageSearchJsonSchemaWidget,
  SparkProductIssueJsonSchemaWidget,
  SparkProductIssueSearchJsonSchemaWidget,
  TermsheetControlJsonSchemaWidget,
  TermsheetControlSearchJsonSchemaWidget,
  XOneDealJsonSchemaWidget,
  XOneDealSearchJsonSchemaWidget,
} from './widgets/WidgetXSource';

export const JsonSchemaWidgets = {
  IC: IcContactsJsonSchemaWidget,
  IC_CLIENT: IcAccountJsonSchemaWidget,
  IC_CLIENT_SEARCH: IcAccountsJsonSchemaWidget,
  IC_LEGAL_ENTITY: IcAccountLegalEntityJsonSchemaWidget,
  IC_LEGAL_ENTITY_SEARCH: IcAccountsLegalEntityJsonSchemaWidget,
  IC_ELEMENTARY_CLIENT: IcAccountElementaryClientJsonSchemaWidget,
  IC_ELEMENTARY_CLIENT_SEARCH: IcAccountsElementaryClientJsonSchemaWidget,
  SPARK_MONTAGE: SparkMontageJsonSchemaWidget,
  SPARK_MONTAGE_SEARCH: SparkMontageSearchJsonSchemaWidget,
  SPARK_PRODUCT_ISSUE: SparkProductIssueJsonSchemaWidget,
  SPARK_PRODUCT_ISSUE_SEARCH: SparkProductIssueSearchJsonSchemaWidget,
  ELIOT_BASKET: EliotBasketJsonSchemaWidget,
  ELIOT_BASKET_SEARCH: EliotBasketSearchJsonSchemaWidget,
  ELIOT_FLOW: EliotFlowJsonSchemaWidget,
  ELIOT_FLOW_SEARCH: EliotFlowSearchJsonSchemaWidget,
  X_ONE_DEAL: XOneDealJsonSchemaWidget,
  X_ONE_DEAL_SEARCH: XOneDealSearchJsonSchemaWidget,
  GALAXY_PRODUCT: GalaxyProductJsonSchemaWidget,
  GALAXY_PRODUCT_SEARCH: GalaxyProductSearchJsonSchemaWidget,
  MAESTRO_COUNTERPARTY: MaestroCounterpartyJsonSchemaWidget,
  MAESTRO_COUNTERPARTY_SEARCH: MaestroCounterpartySearchJsonSchemaWidget,
  MAESTRO_CARE_COUNTERPARTY: MaestroCareCounterpartyJsonSchemaWidget,
  MAESTRO_CARE_COUNTERPARTY_SEARCH: MaestroCareCounterpartySearchJsonSchemaWidget,
  MAESTRO_LEGAL_ENTITY: MaestroLegalEntityJsonSchemaWidget,
  MAESTRO_LEGAL_ENTITY_SEARCH: MaestroLegalEntitySearchJsonSchemaWidget,
  TERMSHEET_CONTROL: TermsheetControlJsonSchemaWidget,
  TERMSHEET_CONTROL_SEARCH: TermsheetControlSearchJsonSchemaWidget,
  DATE: DateJsonSchemaWidget,
  DATE_SEARCH: DateRangeJsonSchemaWidget,
  BOOLEAN: BooleanJsonSchemaWidget,
};

const JsonSchemaFormatters: any = {
  IC: flattenIcContacts,
};

const assertWidget = (xSource: string): void => {
  for (const xSourceKey in JsonSchemaWidgets) {
    if (xSource === xSourceKey) {
      return;
    }
  }
  throw new Error('X-SOURCE Widget not found !');
};

const assertFormatter = (xSource: string): void => {
  for (const xSourceKey in JsonSchemaFormatters) {
    if (xSource === xSourceKey) {
      return;
    }
  }
  throw new Error('X-SOURCE Formatter not found !');
};

const hasFieldError = (classNames: string): boolean => {
  return classNames.indexOf('has-error') !== -1 || classNames.indexOf('has-danger') !== -1;
};

const defaultFieldTemplate = (props: FieldTemplateProps): JSX.Element => {
  const { id, label, required, classNames, help, description, errors, uiSchema, children } = props;
  return (
    <div className={`${classNames} col-md-${uiSchema['ui:columns'] || 12}`}>
      <label className={`col-form-label mb-2 ${hasFieldError(classNames) ? 'text-danger' : ''}`} htmlFor={id}>
        {label}
        {required || uiSchema['ui:widget'] === 'BOOLEAN' ? '' : ' (optional)'}
      </label>
      {description}
      {children}
      {errors}
      {help}
    </div>
  );
};

const searchDefaultFieldTemplate = (props: FieldTemplateProps): JSX.Element => {
  const { id, label, classNames, help, description, errors, uiSchema, children } = props;
  return (
    <div className={`${classNames} col-md-${uiSchema['ui:columns'] || 12}`}>
      <label className={`col-form-label mb-2 ${hasFieldError(classNames) ? 'text-danger' : ''}`} htmlFor={id}>
        {label}
      </label>
      {description}
      {children}
      {errors}
      {help}
    </div>
  );
};

export const computeSchema = (jsonSchema: any, isSearch?: boolean): any => {
  if (!jsonSchema) {
    return {};
  }
  const schema = JSON.parse(JSON.stringify(jsonSchema));
  if (isSearch) {
    schema.required = [];
  }
  delete schema.title;
  delete schema.description;
  delete schema.additionalProperties;
  for (const key in schema.properties) {
    if (
      [
        EIcMetadataXSource.IC_LEGAL_ENTITY,
        EIcMetadataXSource.IC_ELEMENTARY_CLIENT,
        ...JEDEAI_METADATA_X_SOURCE_LIST,
      ].indexOf(schema.properties[key]['x-source']) !== -1
    ) {
      if (isSearch) {
        delete schema.properties[key].default;
      }
      delete schema.properties[key].properties;
      delete schema.properties[key].description;
      schema.properties[key].type = 'string';
    } else if (schema.properties[key].type === 'object' || schema.properties[key].properties !== undefined) {
      delete schema.properties[key];
    } else {
      if (isSearch) {
        delete schema.properties[key].default;
      } else {
        if (schema.properties[key].type === 'number' && schema.properties[key].default !== undefined) {
          schema.properties[key].default = parseInt(schema.properties[key].default);
        }
        if (schema.properties[key].type === 'boolean' && schema.properties[key].default !== undefined) {
          schema.properties[key].default = Boolean(JSON.parse(schema.properties[key]?.default));
        }
      }
      delete schema.properties[key].description;
    }
  }
  return schema;
};

export const computeUiSchema = (jsonSchema: any, numberOfColumns: number, isSearch?: boolean): UiSchema => {
  const uiSchema: UiSchema = {};

  if (jsonSchema) {
    const colSize = 12 / numberOfColumns;

    for (const key in jsonSchema.properties) {
      uiSchema[key] = {
        'ui:FieldTemplate': isSearch ? searchDefaultFieldTemplate : defaultFieldTemplate,
        'ui:columns': colSize,
      };

      if (jsonSchema.properties[key]['x-source']) {
        try {
          assertWidget(jsonSchema.properties[key]['x-source']);
          uiSchema[key]['ui:widget'] = isSearch
            ? `${jsonSchema.properties[key]['x-source']}_SEARCH`
            : jsonSchema.properties[key]['x-source'];
        } catch (e) {}
      }
      if (jsonSchema.properties[key]['type'] === 'array') {
        uiSchema[key]['ui:options'] = { orderable: false };
      } else if (jsonSchema.properties[key]['type'] === 'boolean') {
        uiSchema[key]['ui:widget'] = 'BOOLEAN';
      } else if (jsonSchema.properties[key]['format'] === 'date') {
        uiSchema[key]['ui:widget'] = isSearch ? 'DATE_SEARCH' : 'DATE';
      }
    }
  }

  return uiSchema;
};

export const flattenFormData = (jsonSchema: any, formData: any, isSearch?: boolean) => {
  const flattenedFormData = { ...formData };

  for (const key in jsonSchema.properties) {
    if (jsonSchema.properties[key]['type'] === 'boolean') {
      flattenedFormData[key] = formData[key] === undefined ? false : formData[key];
    } else if (jsonSchema.properties[key]['format'] === 'date' && !isSearch) {
      flattenedFormData[key] = formData[key] ? formData[key].split('T')[0] : undefined;
    } else if (jsonSchema.properties[key]['x-source']) {
      try {
        const xSource = jsonSchema.properties[key]['x-source'];
        assertFormatter(xSource);
        flattenedFormData[key] = `${JsonSchemaFormatters[xSource](formData[key])}`;
      } catch (e) {}
    }
  }
  return flattenedFormData;
};
